/**
 * A small, safe expression language for clinical rule conditions, readable by clinicians:
 *
 *   sbp >= 140 or dbp >= 90
 *   htn_step == 'step2' and not bp_controlled and days_since_med_change >= 28
 *   oneof(bp_grade, 'g2', 'g3') and not exists(cvd_risk_pct)
 *
 * No `eval`, no property access, no loops: only literals, variables from the context, the
 * operators below and a fixed list of functions. Parsed once at pack-build time.
 *
 * Missing values: a variable that is absent is `null`. Comparing `null` with anything (except
 * `== null` / `!= null`) is FALSE, and arithmetic with `null` gives `null`. A rule therefore never
 * fires on a value nobody recorded; test for absence explicitly with `exists(x)` / `not exists(x)`.
 */
export class ExprError extends Error {
    source;
    pos;
    constructor(message, source, pos) {
        super(`${message} at position ${pos} in: ${source}`);
        this.source = source;
        this.pos = pos;
        this.name = 'ExprError';
    }
}
const FUNCTIONS = {
    /** True when the value was recorded. */
    exists: (args) => args[0] !== null && args[0] !== undefined,
    /** True when the first argument equals any of the others. */
    oneof: (args) => args[0] !== null && args.slice(1).some((v) => v === args[0]),
    min: (args) => numeric(args, (xs) => Math.min(...xs)),
    max: (args) => numeric(args, (xs) => Math.max(...xs)),
    abs: (args) => numeric(args.slice(0, 1), (xs) => Math.abs(xs[0])),
    /** First argument that was recorded. */
    coalesce: (args) => args.find((v) => v !== null && v !== undefined) ?? null,
    /** has(list, item): a multiple-choice value ("alcohol,khat") contains the item. */
    has: (args) => typeof args[0] === 'string' &&
        args[0]
            .split(',')
            .map((x) => x.trim())
            .includes(String(args[1])),
    /** Whole part (towards minus infinity). */
    floor: (args) => numeric(args.slice(0, 1), (xs) => Math.floor(xs[0])),
    /** Rounded to `digits` decimals (default 0), half up: round(2.25, 1) = 2.3. */
    round: (args) => {
        const [x, d] = args;
        if (typeof x !== 'number' || (d !== undefined && typeof d !== 'number'))
            return null;
        const k = 10 ** (d ?? 0);
        return Math.round(x * k) / k;
    },
    /** if(condition, then, else): `else` when the condition is false or unknown. */
    if: (args) => (args[0] === true ? (args[1] ?? null) : (args[2] ?? null)),
    /** lookup(key, k1, v1, k2, v2, ...): the value paired with the key; null when none matches. */
    lookup: (args) => {
        const key = args[0];
        if (key === null || key === undefined)
            return null;
        for (let i = 1; i + 1 < args.length; i += 2)
            if (args[i] === key)
                return args[i + 1] ?? null;
        return null;
    },
    /** contains(text, a, b, ...): the text contains any of the words (case-insensitive). */
    contains: (args) => found(args) !== null,
    /**
     * found(text, a, b, ...): the words the text contains (case-insensitive, plain substring), in
     * the order given, joined by ", "; null when none. For "medicines flagged for review".
     */
    found: (args) => found(args),
    /**
     * meaningful(text): the text, or null when it says nothing ("none", "nil", "no", "n/a", "-",
     * "nothing", "negative", blank). Free text that says "none" is not a finding.
     */
    meaningful: (args) => {
        const v = args[0];
        if (typeof v !== 'string')
            return v ?? null;
        return NOTHING.has(v.trim().toLowerCase()) ? null : v;
    },
    /** left(text, n): the first n characters of a text. */
    left: (args) => typeof args[0] === 'string' && typeof args[1] === 'number' ? args[0].slice(0, args[1]) : null,
    /** join(separator, a, b, ...): the recorded values, joined; null when none. */
    join: (args) => {
        const parts = args.slice(1).filter((v) => v !== null && v !== undefined && v !== '');
        return parts.length ? parts.map(String).join(String(args[0] ?? '')) : null;
    },
    /** add_days(date, n): a YYYY-MM-DD date n days later. */
    add_days: (args) => {
        const d = isoDay(args[0] ?? null);
        return d === null || typeof args[1] !== 'number' ? null : dayIso(d + Math.round(args[1]));
    },
    /** add_months(date, n): n calendar months later, clamped to the month's end (30 Nov + 3 = 28 Feb). */
    add_months: (args) => {
        const p = isoParts(args[0] ?? null);
        const n = args[1];
        if (!p || typeof n !== 'number')
            return null;
        const total = p.y * 12 + (p.m - 1) + Math.round(n);
        const y = Math.floor(total / 12);
        const m = (total % 12) + 1;
        const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
        return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(Math.min(p.d, last)).padStart(2, '0')}`;
    },
    /** days_between(a, b): whole days from date a to date b (negative when b is earlier). */
    days_between: (args) => {
        const a = isoDay(args[0] ?? null);
        const b = isoDay(args[1] ?? null);
        return a === null || b === null ? null : b - a;
    },
};
const NOTHING = new Set([
    '',
    'none',
    'nil',
    'no',
    'n/a',
    'na',
    '-',
    'nothing',
    'negative',
    'none.',
]);
function found(args) {
    const text = args[0];
    if (typeof text !== 'string' || !text)
        return null;
    const s = text.toLowerCase();
    const hits = args
        .slice(1)
        .filter((w) => typeof w === 'string' && w && s.includes(w.toLowerCase()));
    return hits.length ? hits.join(', ') : null;
}
function isoParts(v) {
    if (typeof v !== 'string')
        return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    if (!m)
        return null;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const t = new Date(Date.UTC(y, mo - 1, d));
    return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
        ? { y, m: mo, d }
        : null;
}
const isoDay = (v) => {
    const p = isoParts(v);
    return p ? Date.UTC(p.y, p.m - 1, p.d) / 86400000 : null;
};
const dayIso = (n) => new Date(n * 86400000).toISOString().slice(0, 10);
const ARITY = {
    exists: [1, 1],
    oneof: [2, 50],
    min: [1, 50],
    max: [1, 50],
    abs: [1, 1],
    coalesce: [1, 50],
    floor: [1, 1],
    has: [2, 2],
    round: [1, 2],
    if: [3, 3],
    lookup: [3, 99],
    contains: [2, 60],
    found: [2, 60],
    meaningful: [1, 1],
    join: [2, 30],
    left: [2, 2],
    add_days: [2, 2],
    add_months: [2, 2],
    days_between: [2, 2],
};
function numeric(args, f) {
    if (!args.length || args.some((a) => typeof a !== 'number'))
        return null;
    return f(args);
}
const KEYWORDS = new Set(['and', 'or', 'not', 'true', 'false', 'null']);
function tokenize(src) {
    const out = [];
    let i = 0;
    while (i < src.length) {
        const c = src.charAt(i);
        if (/\s/.test(c)) {
            i++;
            continue;
        }
        if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src.charAt(i + 1)))) {
            const m = /^[0-9]*\.?[0-9]+/.exec(src.slice(i));
            if (!m)
                throw new ExprError('Bad number', src, i);
            out.push({ k: 'num', v: Number(m[0]), p: i });
            i += m[0].length;
            continue;
        }
        if (c === "'") {
            const end = src.indexOf("'", i + 1);
            if (end < 0)
                throw new ExprError('Unclosed string', src, i);
            out.push({ k: 'str', v: src.slice(i + 1, end), p: i });
            i = end + 1;
            continue;
        }
        if (/[A-Za-z_]/.test(c)) {
            const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
            const word = m?.[0] ?? c;
            out.push({ k: 'id', v: word, p: i });
            i += word.length;
            continue;
        }
        const two = src.slice(i, i + 2);
        if (['==', '!=', '<=', '>='].includes(two)) {
            out.push({ k: 'op', v: two, p: i });
            i += 2;
            continue;
        }
        if ('<>+-*/(),'.includes(c)) {
            out.push({ k: 'op', v: c, p: i });
            i++;
            continue;
        }
        if (c === '=')
            throw new ExprError("Use '==' to compare", src, i);
        throw new ExprError(`Unexpected character '${c}'`, src, i);
    }
    out.push({ k: 'end', p: src.length });
    return out;
}
// ------------------------------------------------------------------ parser (precedence climbing)
export function parse(src) {
    if (src.length > 2000)
        throw new ExprError('Expression too long', src, 0);
    const toks = tokenize(src);
    let i = 0;
    const peek = () => toks[i];
    const next = () => toks[i++];
    const isOp = (v) => {
        const t = peek();
        return t.k === 'op' && t.v === v;
    };
    const isWord = (w) => {
        const t = peek();
        return t.k === 'id' && t.v === w;
    };
    const expectOp = (v) => {
        const t = next();
        if (t.k !== 'op' || t.v !== v)
            throw new ExprError(`Expected '${v}'`, src, t.p);
    };
    const orExpr = () => {
        let a = andExpr();
        while (isWord('or')) {
            next();
            a = { t: 'or', a, b: andExpr() };
        }
        return a;
    };
    const andExpr = () => {
        let a = notExpr();
        while (isWord('and')) {
            next();
            a = { t: 'and', a, b: notExpr() };
        }
        return a;
    };
    const notExpr = () => {
        if (isWord('not')) {
            next();
            return { t: 'not', e: notExpr() };
        }
        return cmpExpr();
    };
    const cmpExpr = () => {
        const a = addExpr();
        const t = peek();
        if (t.k === 'op' && ['==', '!=', '<', '<=', '>', '>='].includes(t.v)) {
            next();
            const b = addExpr();
            const after = peek();
            if (after.k === 'op' && ['==', '!=', '<', '<=', '>', '>='].includes(after.v)) {
                throw new ExprError("Chained comparison: write 'a <= x and x < b'", src, after.p);
            }
            return { t: 'cmp', op: t.v, a, b };
        }
        return a;
    };
    const addExpr = () => {
        let a = mulExpr();
        for (;;) {
            const t = peek();
            if (t.k === 'op' && (t.v === '+' || t.v === '-')) {
                next();
                a = { t: 'arith', op: t.v, a, b: mulExpr() };
            }
            else
                return a;
        }
    };
    const mulExpr = () => {
        let a = unary();
        for (;;) {
            const t = peek();
            if (t.k === 'op' && (t.v === '*' || t.v === '/')) {
                next();
                a = { t: 'arith', op: t.v, a, b: unary() };
            }
            else
                return a;
        }
    };
    const unary = () => {
        if (isOp('-')) {
            next();
            return { t: 'neg', e: unary() };
        }
        return primary();
    };
    const primary = () => {
        const t = next();
        if (t.k === 'num')
            return { t: 'lit', v: t.v };
        if (t.k === 'str')
            return { t: 'lit', v: t.v };
        if (t.k === 'op' && t.v === '(') {
            const e = orExpr();
            expectOp(')');
            return e;
        }
        if (t.k === 'id') {
            if (t.v === 'true')
                return { t: 'lit', v: true };
            if (t.v === 'false')
                return { t: 'lit', v: false };
            if (t.v === 'null')
                return { t: 'lit', v: null };
            if (KEYWORDS.has(t.v))
                throw new ExprError(`Unexpected '${t.v}'`, src, t.p);
            if (isOp('(')) {
                next();
                if (!(t.v in FUNCTIONS))
                    throw new ExprError(`Unknown function '${t.v}'`, src, t.p);
                const fn = t.v;
                const args = [];
                if (!isOp(')')) {
                    args.push(orExpr());
                    while (isOp(',')) {
                        next();
                        args.push(orExpr());
                    }
                }
                expectOp(')');
                const [lo, hi] = ARITY[fn];
                if (args.length < lo || args.length > hi) {
                    throw new ExprError(`${fn}() takes ${lo === hi ? lo : `${lo}-${hi}`} argument(s)`, src, t.p);
                }
                return { t: 'call', fn, args };
            }
            return { t: 'var', name: t.v };
        }
        throw new ExprError(t.k === 'end' ? 'Unexpected end' : 'Unexpected token', src, t.p);
    };
    const ast = orExpr();
    const t = peek();
    if (t.k !== 'end')
        throw new ExprError('Unexpected trailing input', src, t.p);
    return ast;
}
// ------------------------------------------------------------------ evaluation
const truthy = (v) => v === true || (typeof v === 'number' && v !== 0) || (typeof v === 'string' && v !== '');
export function evaluate(ast, ctx) {
    switch (ast.t) {
        case 'lit':
            return ast.v;
        case 'var': {
            const v = ctx[ast.name];
            return v === undefined ? null : v;
        }
        case 'not':
            return !truthy(evaluate(ast.e, ctx));
        case 'neg': {
            const v = evaluate(ast.e, ctx);
            return typeof v === 'number' ? -v : null;
        }
        case 'and':
            return truthy(evaluate(ast.a, ctx)) && truthy(evaluate(ast.b, ctx));
        case 'or':
            return truthy(evaluate(ast.a, ctx)) || truthy(evaluate(ast.b, ctx));
        case 'cmp': {
            const a = evaluate(ast.a, ctx);
            const b = evaluate(ast.b, ctx);
            if (ast.op === '==')
                return a === b;
            if (ast.op === '!=')
                return a !== b;
            if (a === null || b === null || typeof a !== typeof b || typeof a === 'boolean')
                return false;
            switch (ast.op) {
                case '<':
                    return a < b;
                case '<=':
                    return a <= b;
                case '>':
                    return a > b;
                case '>=':
                    return a >= b;
            }
            return false;
        }
        case 'arith': {
            const a = evaluate(ast.a, ctx);
            const b = evaluate(ast.b, ctx);
            if (typeof a !== 'number' || typeof b !== 'number')
                return null;
            switch (ast.op) {
                case '+':
                    return a + b;
                case '-':
                    return a - b;
                case '*':
                    return a * b;
                case '/':
                    return b === 0 ? null : a / b;
            }
            return null;
        }
        case 'call':
            return FUNCTIONS[ast.fn](ast.args.map((x) => evaluate(x, ctx)));
    }
}
export const test = (ast, ctx) => truthy(evaluate(ast, ctx));
/** Variables an expression reads (to check them against the pack's declared context). */
export function variablesOf(ast, acc = new Set()) {
    switch (ast.t) {
        case 'var':
            acc.add(ast.name);
            break;
        case 'not':
        case 'neg':
            variablesOf(ast.e, acc);
            break;
        case 'and':
        case 'or':
        case 'cmp':
        case 'arith':
            variablesOf(ast.a, acc);
            variablesOf(ast.b, acc);
            break;
        case 'call':
            for (const a of ast.args)
                variablesOf(a, acc);
            break;
        case 'lit':
            break;
    }
    return acc;
}
//# sourceMappingURL=expr.js.map